import type { ReactNode } from "react";
import { Badge } from "./badge";
import { breiteKlasse, cx } from "./fortschritt/breite";
import type { BausteinSymbol } from "./symbol-typ";

/** Vorgabe-Symbol: Lucide `ChartBarBig` als Inline-Pfad (Herkunft: Vorschau M-153, das Paket bindet `lucide-react` nicht ein). */
function BalkenSymbol({ className }: { className?: string }): ReactNode {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
      <rect x="7" y="13" width="9" height="4" rx="1" />
      <rect x="7" y="5" width="12" height="4" rx="1" />
    </svg>
  );
}

/**
 * Eine Zeile der Balkenliste. Der Baustein zeichnet nur; welche Zahl rechts steht und ob eine Gruppe als „hell“ gilt
 * (Kleinwert-Regel r179, Mindestmengen), entscheidet der Aufrufer. Der Baustein rechnet keine Mindestmengen.
 */
export interface BalkenGruppe {
  label: string;
  /**
   * Messwert der Gruppe. Die Balkenlänge ist relativ zur größten Gruppe des Abschnitts (AW-4, M-153), solange `anteil` fehlt.
   * Ohne `wert` und ohne `anteil` bleibt die Spur leer.
   */
  wert?: number;
  /** Balkenlänge in Prozent der Spur (0 bis 100); setzt die Längen-Rechnung aus `wert` außer Kraft (z. B. Anteil an der Gesamtzahl). */
  anteil?: number;
  /**
   * Heller Balken (r179, M-153 AW-5): nur ungefähr, ohne Wert für Hilfstechnik (kein `progressbar`, kein `aria-valuenow`).
   * Die Länge bleibt Sache des Aufrufers (`anteil`) oder folgt `wert`.
   */
  hell?: boolean;
  /** Leere Spur ohne Füllbalken (z. B. „vermutlich nicht geliefert“); kein `progressbar`. */
  leer?: boolean;
  /** Kleiner grauer Zusatz hinter der Beschriftung (z. B. technischer Feldname, „3 Fehlerarten mit je unter 5 Dokumenten“). */
  zusatz?: string;
  /** Text rechts über dem Balken, frei vom Aufrufer (z. B. „148 · 38 %“, „unter 5“). Fehlt er, steht der Wert mit deutscher Zahlform. */
  textRechts?: string;
  /** Darstellung des Rechtstexts: `stark` (fett), `leise` (klein, grau), `warnung` (Pille). Vorgabe: `leise` bei `hell`/`leer`, sonst `stark`. */
  textRechtsArt?: "stark" | "leise" | "warnung";
}

export interface BalkenAbschnitt {
  /** Zwischenüberschrift (klein, groß geschrieben) über den Zeilen; fehlt sie, steht die Liste ohne Überschrift. */
  titel?: string;
  gruppen: readonly BalkenGruppe[];
}

/** Voller Balken (nur bei genannter Zahl). */
const BALKEN_VOLL =
  "flex flex-col justify-center rounded-full overflow-hidden bg-primary text-xs text-primary-foreground text-center whitespace-nowrap transition duration-500";
/** Heller Balken: nur ungefähr (r179), nie eine Länge, die eine Zahl verrät, solange der Aufrufer `anteil` fest setzt. */
const BALKEN_HELL = "flex flex-col rounded-full bg-primary-300";
const SPUR = "flex w-full h-2 bg-surface rounded-full overflow-hidden";

const ZAHL = new Intl.NumberFormat("de-DE");

function begrenze(prozentwert: number): number {
  if (!Number.isFinite(prozentwert)) return 0;
  return Math.min(100, Math.max(0, prozentwert));
}

/** Größter Messwert eines Abschnitts (Bezug der relativen Länge, AW-4). */
function groessterWert(gruppen: readonly BalkenGruppe[]): number {
  let max = 0;
  for (const g of gruppen) if (typeof g.wert === "number" && g.wert > max) max = g.wert;
  return max;
}

/** Länge in Prozent: `anteil` des Aufrufers, sonst Wert relativ zur größten Gruppe; ohne beides 0. */
function laenge(g: BalkenGruppe, max: number): number {
  if (typeof g.anteil === "number") return begrenze(g.anteil);
  if (typeof g.wert === "number" && max > 0) return begrenze((g.wert / max) * 100);
  return 0;
}

function Balken({ g, max }: { g: BalkenGruppe; max: number }): ReactNode {
  if (g.leer) return <div className={SPUR} aria-hidden="true" />;
  const prozentwert = laenge(g, max);
  if (g.hell) {
    return (
      <div className={SPUR} aria-hidden="true">
        <div className={cx(BALKEN_HELL, breiteKlasse(prozentwert))} />
      </div>
    );
  }
  // Exakter Balken: Wert und Obergrenze für Hilfstechnik; ohne `wert` zählt die Länge in Prozent von 100.
  const nutzeWert = typeof g.wert === "number" && typeof g.anteil !== "number";
  return (
    <div
      className={SPUR}
      role="progressbar"
      aria-label={g.label}
      aria-valuenow={nutzeWert ? g.wert : Math.round(prozentwert)}
      aria-valuemin={0}
      aria-valuemax={nutzeWert ? max : 100}
    >
      <div className={cx(BALKEN_VOLL, breiteKlasse(prozentwert))} />
    </div>
  );
}

function RechtsText({ g }: { g: BalkenGruppe }): ReactNode {
  const text = g.textRechts ?? (typeof g.wert === "number" ? ZAHL.format(g.wert) : null);
  if (text === null) return null;
  const art = g.textRechtsArt ?? (g.hell || g.leer ? "leise" : "stark");
  if (art === "warnung") return <Badge ton="warnung">{text}</Badge>;
  if (art === "stark") return <span className="text-sm font-semibold text-foreground">{text}</span>;
  return <span className="text-xs text-muted-foreground-1">{text}</span>;
}

function Zeile({ g, max }: { g: BalkenGruppe; max: number }): ReactNode {
  return (
    <li>
      <div className="mb-1.5 flex flex-wrap justify-between items-center gap-x-3 gap-y-1">
        <p className="text-sm text-foreground">
          {g.label}
          {g.zusatz !== undefined && <> <span className="ms-1 text-xs text-muted-foreground-1">{g.zusatz}</span></>}
        </p>
        <RechtsText g={g} />
      </div>
      <Balken g={g} max={max} />
    </li>
  );
}

/**
 * Balkenliste in einer Karte (Katalog `response-bar-list-card`, M-006 HRWorks-Befüllung, M-153 Analytics-Auswertung): je Gruppe eine
 * Zeile aus Beschriftung, Rechtstext und Spur mit Balken. Länge relativ zur größten Gruppe (AW-4) oder per `anteil`; heller Balken für
 * Kleinwerte und „Weitere“ (AW-5); Breiten nur über `wp0…wp100` (kein `style=`). Der Baustein prüft keine Rechte und rechnet keine
 * Mindestmengen: Zahl oder „unter 5“ liefert der Aufrufer fertig (r179). Entweder `gruppen` (eine Liste) oder `abschnitte` (mehrere
 * Listen mit Zwischenüberschrift, ab `lg` zwei Spalten).
 */
export function Balkenliste({
  id,
  titel,
  meta,
  symbol: Symbol = BalkenSymbol,
  gruppen,
  abschnitte,
}: {
  id: string;
  titel: string;
  /** Kleine Zeile unter dem Titel, durch Punkte getrennt (Untertitel, Stand, Hinweis zum hellen Balken). */
  meta?: readonly string[];
  /** Lucide-Symbol des Aufrufers links neben dem Titel (Vorgabe: Balkendiagramm). */
  symbol?: BausteinSymbol;
  gruppen?: readonly BalkenGruppe[];
  abschnitte?: readonly BalkenAbschnitt[];
}): ReactNode {
  const titelId = `${id}-titel`;
  const liste: readonly BalkenAbschnitt[] = abschnitte ?? (gruppen ? [{ gruppen }] : []);
  return (
    <>
      {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-progress-cards/response-bar-list-card */}
      <section id={id} aria-labelledby={titelId} className="p-5 flex flex-col bg-card border border-card-line shadow-2xs rounded-xl">
        <div className="flex gap-3">
          <div className="mt-0.5 flex shrink-0 justify-center items-center size-8 bg-surface text-surface-foreground rounded-md">
            <Symbol className="shrink-0 size-4" aria-hidden="true" />
          </div>
          <div className="grow">
            <h2 id={titelId} className="font-semibold text-base text-foreground">
              {titel}
            </h2>
            {meta && meta.length > 0 && (
              <ul className="flex flex-wrap items-center gap-1.5">
                {meta.map((t) => (
                  <li
                    key={t}
                    className="inline-flex items-center relative text-xs text-muted-foreground-1 pe-2 last:pe-0 last:after:hidden after:absolute after:top-1/2 after:inset-e-0 after:inline-block after:size-1 after:bg-surface-3 after:rounded-full after:-translate-y-1/2"
                  >
                    {t}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className={liste.length > 1 ? "mt-5 grid lg:grid-cols-2 gap-x-10 gap-y-6" : "mt-5 grid gap-x-10 gap-y-6"}>
          {liste
            .filter((a) => a.gruppen.length > 0)
            .map((a, i) => {
              const max = groessterWert(a.gruppen);
              return (
                <div key={a.titel ?? i}>
                  {a.titel && <h3 className="mb-3 text-xs uppercase font-medium text-muted-foreground-1">{a.titel}</h3>}
                  <ul className="flex flex-col gap-y-3">
                    {a.gruppen.map((g, j) => (
                      <Zeile key={`${g.label}-${j}`} g={g} max={max} />
                    ))}
                  </ul>
                </div>
              );
            })}
        </div>
      </section>
    </>
  );
}
